import { createApp, defineAsyncComponent } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createBootstrap } from 'bootstrap-vue-next'
import * as BVN from 'bootstrap-vue-next'
import Notifications from '@kyvg/vue3-notification'
import * as Sentry from '@sentry/vue'

import App from './App.vue'
import router from './router'
import request from '@/utils/request'
import { getRoutes } from './menu'
import { useAccountsStore } from '@/stores/accounts'
import { handleAuthentication, login, logout } from '@/auth/AuthService'

import Chart from '@/components/Chart.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiCard from '@/components/ui/UiCard.vue'
import UiCardHeader from '@/components/ui/UiCardHeader.vue'
import UiCardBody from '@/components/ui/UiCardBody.vue'
import UiCardFooter from '@/components/ui/UiCardFooter.vue'
import UiAlert from '@/components/ui/UiAlert.vue'
import UiBadge from '@/components/ui/UiBadge.vue'
import UiProgress from '@/components/ui/UiProgress.vue'
import UiPagination from '@/components/ui/UiPagination.vue'
import UiTabs from '@/components/ui/UiTabs.vue'
import UiTab from '@/components/ui/UiTab.vue'
import UiInput from '@/components/ui/UiInput.vue'
import UiSelect from '@/components/ui/UiSelect.vue'
import UiCheckbox from '@/components/ui/UiCheckbox.vue'
import UiCheckboxGroup from '@/components/ui/UiCheckboxGroup.vue'
import UiInputGroup from '@/components/ui/UiInputGroup.vue'
import UiSearchSelect from '@/components/ui/UiSearchSelect.vue'
import UiModal from '@/components/ui/UiModal.vue'

// bootstrap-vue-next component styles (Bootstrap base CSS is imported via App.vue SCSS)
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'

// Tailwind CSS (tw- prefix, preflight disabled — coexists with Bootstrap)
import '@/assets/tailwind.css'

// MK3 chrome for @zanmato/vue3-treeselect (unscoped overrides applied
// to every consumer — ViewHeader, Hierarchy, Budgets, etc.).
import '@/assets/treeselect-mk3.css'

// Public landing pages — bypass App.vue and the entire auth flow.
// App.vue assumes an authenticated user (loads terms/header/tree stores
// behind a spinner that never clears on 401), so we mount a minimal root
// for these pages instead.
const PUBLIC_PATHS = ['/logout']
const isPublicPath = PUBLIC_PATHS.includes(window.location.pathname)

// Create the app instance
const RootComponent = isPublicPath
  ? defineAsyncComponent(() => import('@/views/Logout.vue'))
  : App
const app = createApp(RootComponent)

// Make app instance available globally for notify helper
window.__vue_app__ = app

// Set up Pinia
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)

const SENTRY_DSN = 'https://e18ada6720490840867018adba0afa99@o4511589316100096.ingest.de.sentry.io/4511589636505680'
if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

app.use(createBootstrap())
app.use(Notifications)

// Register all bootstrap-vue-next components globally
// Templates use kebab-case (b-btn, b-row) which Vue resolves to PascalCase
Object.entries(BVN).forEach(([name, component]) => {
  if (/^B[A-Z]/.test(name) && typeof component === 'object') {
    app.component(name, component as any)
  }
})
// Tailwind UI primitives (replacing BVN equivalents incrementally).
// `Ui` prefix avoids the HTML-reserved `Button` name and matches the
// components/ui/ directory convention.
app.component('UiButton', UiButton)
app.component('UiCard', UiCard)
app.component('UiCardHeader', UiCardHeader)
app.component('UiCardBody', UiCardBody)
app.component('UiCardFooter', UiCardFooter)
app.component('UiAlert', UiAlert)
app.component('UiBadge', UiBadge)
app.component('UiProgress', UiProgress)
app.component('UiPagination', UiPagination)
app.component('UiTabs', UiTabs)
app.component('UiTab', UiTab)
app.component('UiInput', UiInput)
app.component('UiSelect', UiSelect)
app.component('UiCheckbox', UiCheckbox)
app.component('UiCheckboxGroup', UiCheckboxGroup)
app.component('UiInputGroup', UiInputGroup)
app.component('UiSearchSelect', UiSearchSelect)
app.component('UiModal', UiModal)

// Register BVN directives explicitly (v-b-modal, v-b-toggle, v-b-tooltip, v-b-popover)
app.directive('b-modal', BVN.vBModal)
app.directive('b-toggle', BVN.vBToggle)
app.directive('b-tooltip', BVN.vBTooltip)
app.directive('b-popover', BVN.vBPopover)

app.component('Chart', Chart)

if (isPublicPath) {
  // Public root component (Logout.vue) is self-contained — no router,
  // no auth, no API calls. Mount immediately.
  app.mount('#app')
} else {
  handleAuthentication()
    .then(
      () => request.json('/api/auth/'),
      () => login()
    )
    .then((user: any) => {
      const accountsStore = useAccountsStore()
      accountsStore.setUser(user)
      // no user authenticated
      if (!user) return

      Sentry.setUser({ id: user.subject, email: user.email, username: user.name })

      // add routes based on user logged in
      const routes = getRoutes(user)
      routes.forEach((route: any) => router.addRoute(route))

      app.use(router)

      // Wait for the router to resolve the initial navigation before mounting
      router.isReady().then(() => {
        app.mount('#app')
      })
    }, () => {
      alert('User does not exist')
      logout()
      // TODO Add frontend UI when API response is HTTP403 with body "User does not exist."
    })
}
